import {ArrowTopRightOnSquareIcon, MapPinIcon} from "@heroicons/react/20/solid";
import type {SiteEntity} from "@ots-frontend/ots-data";
import type {ReactNode} from "react";
import {OtsIconActionButton} from "../../components/OtsButton";
import {OtsBadge} from "../../components/OtsBadge";
import {DescriptionItem} from "../../components/DescriptionList";
import {OtsDateTime} from "../../components/OtsDateTime";
import type {SiteContainerRow, SiteContainers} from "../site-containers";
import {SiteContainerSummary} from "../SiteContainerSummary";
import {MapInfoFields, MapInfoPanel, MapInfoSection} from "./MapInfoPanel";
import {plainMapEntity, type RenderMapEntity} from "./map-panel";
import {OpsServiceStatusList, OpsStatusBadge, type OpsStatusView} from "./OpsServiceStatusList";
import {SiteTransportIcons} from "./SiteTransportIcons";
import {siteNearbyTitle, SiteNearbyUnits, type SiteNearbyUnitsProps} from "./SiteNearbyUnits";

export type SiteInsightsView = {
  /** Counted from here to now. */
  since: number;
  quantity?: string;
  loads?: string;
  loading?: boolean;
};

export type SiteInfoPanelViewProps = {
  site: SiteEntity;
  /** Leave out while there are no containers at the site; the section is hidden. */
  containers?: SiteContainers;
  now: number;
  ops?: OpsStatusView;
  insights?: SiteInsightsView;
  /** The units the map has drawn lines to; left out when lines are off. */
  nearby?: SiteNearbyUnitsProps;
  /** The site's latest tickets as a whole section, e.g. ConnectedSiteRecentTickets; it fetches its own. */
  recentTickets?: ReactNode;
  renderEntity?: RenderMapEntity;
  onOpenProfile?: () => void;
  onClose?: () => void;
};

/** The live map's panel for a clicked site: what it is, its containers, its ops status, and recent volume. */
export const SiteInfoPanelView = ({
  site,
  containers,
  now,
  ops,
  insights,
  nearby,
  recentTickets,
  renderEntity = plainMapEntity,
  onOpenProfile,
  onClose,
}: SiteInfoPanelViewProps) => {
  const route = site.routes?.[0];
  const containerName = (row: SiteContainerRow): ReactNode => renderEntity({id: row.id, name: row.name, type: "CONTAINER"});
  return <MapInfoPanel
    ariaLabel={`Site ${site.name}`}
    icon={<MapPinIcon/>}
    title={site.name}
    subtitle={site.companyName}
    badges={site.type && <OtsBadge size="compact" tone={site.type.disposalType ? "violet" : "info"}>{site.type.displayValue.defaultMessage}</OtsBadge>}
    actions={onOpenProfile && (
      <OtsIconActionButton variant="text" size="mini" className="!overflow-hidden" label="Open site profile" icon={<ArrowTopRightOnSquareIcon/>} onClick={onOpenProfile}/>
    )}
    onClose={onClose}>
    <MapInfoSection title="Details">
      <MapInfoFields>
        <DescriptionItem label="Operating company">
          {site.companyId && renderEntity({id: site.companyId, name: site.companyName, type: "COMPANY"})}
        </DescriptionItem>
        <DescriptionItem label="Owning company">{site.ownerName}</DescriptionItem>
        <DescriptionItem label="Route">
          {route && renderEntity({id: route.id, name: route.routeGroupName ? `${route.name} (${route.routeGroupName})` : route.name, type: "ROUTE"})}
        </DescriptionItem>
        <DescriptionItem label="Operating area">
          {site.operatingAreaId && renderEntity({id: site.operatingAreaId, name: site.operatingAreaName, type: "OPERATINGAREA"})}
        </DescriptionItem>
        <DescriptionItem label="Transport">
          {/* The container statuses win where the map's site list lags them, so this matches the grid. */}
          <SiteTransportIcons
            waterViaPipeline={site.transportViaPipeline || containers?.waterViaPipeline === true}
            oilViaPipeline={site.transportOilViaPipeline || containers?.oilViaPipeline === true}/>
        </DescriptionItem>
      </MapInfoFields>
    </MapInfoSection>
    {containers && containers.containers.length > 0 && (
      // The busiest section, so it gets more room above and below its heading than the rest.
      <MapInfoSection title="Containers" className="!gap-4 mt-2 !pt-6">
        <SiteContainerSummary summary={containers} now={now} renderName={containerName} className="pb-2"/>
      </MapInfoSection>
    )}
    {nearby && (
      <MapInfoSection title={siteNearbyTitle(nearby.mode)}>
        <SiteNearbyUnits {...nearby}/>
      </MapInfoSection>
    )}
    {recentTickets}
    {ops && ops.serviceTypes.length > 0 && (
      <MapInfoSection
        title="Operations"
        aside={<>
          <OpsStatusBadge status={ops.status} label={ops.label}/>
          {ops.asOf != null && <span className="text-[11px] text-[var(--ots-text-muted)]">Updated <OtsDateTime value={ops.asOf}/></span>}
        </>}>
        <OpsServiceStatusList serviceTypes={ops.serviceTypes}/>
      </MapInfoSection>
    )}
    {insights && (
      <MapInfoSection
        title="Recent activity"
        aside={<span className="text-[11px] text-[var(--ots-text-muted)]">Since <OtsDateTime value={insights.since} displayTime={false}/></span>}>
        {insights.loading
          ? <p className="!m-0 text-xs text-[var(--ots-text-muted)]">Loading…</p>
          : <MapInfoFields>
            <DescriptionItem label="Quantity">{insights.quantity ?? "N/A"}</DescriptionItem>
            <DescriptionItem label="Loads">{insights.loads ?? "N/A"}</DescriptionItem>
          </MapInfoFields>}
      </MapInfoSection>
    )}
  </MapInfoPanel>;
};
